import React, { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  Flex,
  FlexItem,
  FormSelect,
  FormSelectOption,
  Label,
  LabelGroup,
  Spinner,
} from '@patternfly/react-core';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';

const QueueHeadItemSchema = z.object({
  ticket_id: z.string(),
  score: z.number(),
  position: z.number().int().nonnegative(),
});

const ActiveLeaseSchema = z.object({
  session_id: z.string().nullable().optional(),
  ticket_id: z.string().nullable().optional(),
  user_id: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
});

const ActiveSessionSchema = z.object({
  session_id: z.string(),
  state: z.string(),
  user_id: z.string().nullable().optional(),
  reserved_at: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
});

const QueueSnapshotSchema = z.object({
  queue_key: z.string(),
  robot_id: z.string().optional(),
  length: z.number().int().nonnegative(),
  waiting_length: z.number().int().nonnegative().optional(),
  effective_length: z.number().int().nonnegative().optional(),
  has_active_lease: z.boolean().optional(),
  has_active_session: z.boolean().optional(),
  active_lease: ActiveLeaseSchema.nullable().optional(),
  active_session: ActiveSessionSchema.nullable().optional(),
  head: z.array(QueueHeadItemSchema),
});

const ForceClearResponseSchema = z.object({
  ok: z.boolean(),
  robot_id: z.string(),
  cleared_lease: z.boolean(),
  ended_session: z.boolean(),
  session_id: z.string().nullable().optional(),
  ticket_id: z.string().nullable().optional(),
});

type QueueSnapshot = z.infer<typeof QueueSnapshotSchema>;

type RobotOption = {
  id: string;
  name?: string | null;
  type?: string | null;
  status?: string | null;
};

const RAW_QUEUE_BASE_URL = process.env.REACT_APP_QUEUE_URL || '';
const QUEUE_BASE_URL = RAW_QUEUE_BASE_URL.replace(/\/$/, '');
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const FORCE_CLEAR_REASON = 'dashboard_force_clear';

const QueueManager = () => {
  const authedFetch = useAuthedFetch();
  const [robots, setRobots] = useState<RobotOption[]>([]);
  const [robotsLoading, setRobotsLoading] = useState(true);
  const [robotId, setRobotId] = useState('');
  const [snapshot, setSnapshot] = useState<QueueSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const canLoad = Boolean(QUEUE_BASE_URL);

  useEffect(() => {
    let canceled = false;

    const loadRobots = async () => {
      setRobotsLoading(true);
      try {
        const response = await authedFetch(`${API_BASE}/api/robot/`);
        if (!response.ok) {
          throw new Error(`Failed to load robots: ${response.status}`);
        }
        const data = (await response.json()) as RobotOption[];
        if (canceled) {
          return;
        }
        const filteredRobots = data.filter((robot) => {
          const normalizedType = (robot.type || '').toLowerCase();
          return !normalizedType.includes('operator');
        });
        setRobots(filteredRobots);
        setRobotId((currentRobotId) => currentRobotId || filteredRobots[0]?.id || '');
      } catch (err) {
        if (canceled) {
          return;
        }
        const errorMessage = err instanceof Error ? err.message : 'Failed to load robots';
        setSnapshotError(errorMessage);
      } finally {
        if (!canceled) {
          setRobotsLoading(false);
        }
      }
    };

    void loadRobots();

    return () => {
      canceled = true;
    };
  }, [authedFetch]);

  const loadSnapshot = useCallback(async () => {
    if (!QUEUE_BASE_URL) {
      setSnapshotError('Missing REACT_APP_QUEUE_URL');
      return;
    }

    const normalizedRobotId = robotId.trim();
    if (!normalizedRobotId) {
      setSnapshotError('Robot ID is required');
      setSnapshot(null);
      return;
    }

    setSnapshotLoading(true);
    setSnapshotError(null);

    try {
      const queueStateUrl = `${QUEUE_BASE_URL}/api/admin/queue/state?robot_id=${encodeURIComponent(normalizedRobotId)}`;
      const response = await authedFetch(queueStateUrl);
      if (!response.ok) {
        throw new Error(`Queue Manager error: ${response.status}`);
      }
      const raw = await response.json();
      const parsed = QueueSnapshotSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error('Queue Manager returned invalid queue state');
      }
      setSnapshot(parsed.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load queue state';
      setSnapshotError(errorMessage);
    } finally {
      setSnapshotLoading(false);
    }
  }, [authedFetch, robotId]);

  const refreshSnapshot = useCallback(() => {
    void loadSnapshot();
  }, [loadSnapshot]);

  const forceClearActiveTurn = useCallback(async () => {
    if (!QUEUE_BASE_URL) {
      setSnapshotError('Missing REACT_APP_QUEUE_URL');
      return;
    }

    const normalizedRobotId = robotId.trim();
    if (!normalizedRobotId) {
      setSnapshotError('Robot ID is required');
      return;
    }

    const confirmationTarget = snapshot?.active_session?.session_id || normalizedRobotId;
    const confirmationMessage = `Force clear active turn for ${confirmationTarget}? This will end the active session and clear lease state for the selected robot.`;
    const confirmed = window.confirm(confirmationMessage);
    if (!confirmed) {
      return;
    }

    setActionLoading(true);
    setSnapshotError(null);
    setActionResult(null);

    try {
      const requestBody = {
        robot_id: normalizedRobotId,
        reason: FORCE_CLEAR_REASON,
      };
      const response = await authedFetch(`${QUEUE_BASE_URL}/api/admin/queue/force_clear_active_turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        throw new Error(`Force clear failed: ${response.status}`);
      }
      const raw = await response.json();
      const parsed = ForceClearResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error('Queue Manager returned invalid force-clear response');
      }
      const clearedLease = parsed.data.cleared_lease;
      const endedSession = parsed.data.ended_session;
      const leaseState = clearedLease ? 'lease cleared' : 'no lease to clear';
      const sessionState = endedSession ? 'session ended' : 'no session ended';
      const resultMessage = `Force clear complete: ${sessionState}, ${leaseState}.`;
      setActionResult(resultMessage);
      await loadSnapshot();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to force clear active turn';
      setSnapshotError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [authedFetch, loadSnapshot, robotId, snapshot?.active_session?.session_id]);

  useEffect(() => {
    if (!canLoad || !robotId || robotsLoading) {
      return;
    }
    void loadSnapshot();
  }, [canLoad, robotId, robotsLoading, loadSnapshot]);

  if (!canLoad) {
    return (
      <Card>
        <CardBody>
          <Alert isInline variant="danger" title="Queue Manager URL missing">
            Set `REACT_APP_QUEUE_URL` to point at the Queue Manager.
          </Alert>
        </CardBody>
      </Card>
    );
  }

  const hasSnapshot = Boolean(snapshot);
  const hasActiveTurn = Boolean(snapshot?.has_active_lease || snapshot?.has_active_session);
  const refreshDisabled = snapshotLoading || actionLoading;
  const forceClearDisabled = snapshotLoading || actionLoading || !hasSnapshot || !hasActiveTurn;

  return (
    <Card>
      <CardTitle>Queue Manager</CardTitle>
      <CardBody>
        <Flex gap={{ default: 'gapMd' }} alignItems={{ default: 'alignItemsCenter' }}>
          <FlexItem>
            <FormSelect
              value={robotId}
              onChange={(_, value) => {
                setRobotId(value);
                setSnapshot(null);
              }}
              aria-label="Robot ID"
              isDisabled={robotsLoading || robots.length === 0}
            >
              {robots.length === 0 ? (
                <FormSelectOption value="" label={robotsLoading ? 'Loading robots...' : 'No robots'} />
              ) : (
                robots.map((robot) => {
                  const optionLabel = robot.name ? `${robot.name} (${robot.id})` : robot.id;
                  return <FormSelectOption key={robot.id} value={robot.id} label={optionLabel} />;
                })
              )}
            </FormSelect>
          </FlexItem>
          <FlexItem>
            <Button onClick={refreshSnapshot} isDisabled={refreshDisabled}>
              Refresh
            </Button>
          </FlexItem>
          <FlexItem>
            <Button variant="danger" onClick={forceClearActiveTurn} isDisabled={forceClearDisabled}>
              Force Clear Active Turn
            </Button>
          </FlexItem>
        </Flex>

        {snapshotLoading && (
          <Bullseye style={{ minHeight: 120 }}>
            <Spinner />
          </Bullseye>
        )}

        {snapshotError && (
          <Alert isInline variant="danger" title="Queue Manager error">
            {snapshotError}
          </Alert>
        )}

        {actionResult && (
          <Alert isInline variant="success" title="Queue action complete">
            {actionResult}
          </Alert>
        )}

        {!snapshotLoading && !snapshotError && !snapshot && (
          <Alert isInline variant="info" title="Select a robot">
            Choose a robot to load its queue state.
          </Alert>
        )}

        {snapshot && !snapshotLoading && (
          <>
            <Flex gap={{ default: 'gapMd' }} style={{ marginTop: 16 }}>
              <FlexItem>
                <Label color="blue">Queue</Label>
                <div>{snapshot.queue_key}</div>
              </FlexItem>
              <FlexItem>
                <Label color="green">Length</Label>
                <div>{snapshot.effective_length ?? snapshot.length}</div>
              </FlexItem>
              <FlexItem>
                <Label color="teal">Waiting</Label>
                <div>{snapshot.waiting_length ?? snapshot.length}</div>
              </FlexItem>
              <FlexItem>
                <Label color={snapshot.has_active_lease ? 'orange' : 'grey'}>Lease Active</Label>
                <div>{snapshot.has_active_lease ? 'Yes' : 'No'}</div>
              </FlexItem>
              <FlexItem>
                <Label color={snapshot.has_active_session ? 'orange' : 'grey'}>Session Active</Label>
                <div>{snapshot.has_active_session ? 'Yes' : 'No'}</div>
              </FlexItem>
              <FlexItem>
                <Label color="orange">Robot</Label>
                <div>{snapshot.robot_id || robotId}</div>
              </FlexItem>
            </Flex>

            {snapshot.active_session && (
              <Flex gap={{ default: 'gapMd' }} style={{ marginTop: 12 }}>
                <FlexItem>
                  <Label color="orange">Session</Label>
                  <div>{snapshot.active_session.session_id}</div>
                </FlexItem>
                <FlexItem>
                  <Label color="orange">Session State</Label>
                  <div>{snapshot.active_session.state}</div>
                </FlexItem>
                <FlexItem>
                  <Label color="orange">Started</Label>
                  <div>{snapshot.active_session.started_at || '—'}</div>
                </FlexItem>
              </Flex>
            )}

            {snapshot.active_lease && (
              <Flex gap={{ default: 'gapMd' }} style={{ marginTop: 12 }}>
                <FlexItem>
                  <Label color="orange">Lease Session</Label>
                  <div>{snapshot.active_lease.session_id || '—'}</div>
                </FlexItem>
                <FlexItem>
                  <Label color="orange">Lease Expires</Label>
                  <div>{snapshot.active_lease.expires_at || '—'}</div>
                </FlexItem>
              </Flex>
            )}

            <div style={{ marginTop: 16 }}>
              <Label>Head</Label>
              <LabelGroup style={{ marginTop: 8 }}>
                {snapshot.head.length === 0 && <Label color="grey">Empty</Label>}
                {snapshot.head.map((item) => (
                  <Label key={item.ticket_id} color="purple">
                    #{item.position} {item.ticket_id}
                  </Label>
                ))}
              </LabelGroup>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default QueueManager;
